import { useState, useEffect, useRef } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import CalculatorPage from './components/CalculatorPage';
import Blog from './components/Blog';
import BlogPost from './components/BlogPost';
import FAQ from './components/FAQ';
import Resources from './components/Resources';
import About from './components/About';
import Disclaimer from './components/Disclaimer';
import Footer from './components/Footer';

function App() {
    const location = useLocation();
    const [loanData, setLoanData] = useState({
        homePrice: 500000,
        downPayment: 100000,
        downPaymentPercent: 20,
        interestRate: 6.5,
        loanYears: 30,
        zipCode: '90210',
        loanType: '30-year-fixed',
        buydownType: 'none'
    });

    // State for manual overrides of calculated costs
    const [manualOverrides, setManualOverrides] = useState({
        propertyTax: null,
        propertyTaxPercent: null,
        insurance: null,
        pmi: null
    });

    // State for loan comparisons
    const [comparisons, setComparisons] = useState([]);

    // State for prepayment calculator (to persist data for save/load)
    const [prepaymentData, setPrepaymentData] = useState({
        extraPayment: 0
    });

    // State for Amortization Schedule (lifted for persistence)
    const [amortizationViewMode, setAmortizationViewMode] = useState('yearly');
    const [amortizationYear, setAmortizationYear] = useState(1);

    const fileInputRef = useRef(null);

    // Check for shared configuration in URL on mount
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const configParam = params.get('config');
        if (configParam) {
            try {
                const jsonString = atob(decodeURIComponent(configParam));
                const config = JSON.parse(jsonString);

                if (config.loanData) setLoanData(config.loanData);
                if (config.manualOverrides) setManualOverrides(config.manualOverrides);
                if (config.comparisons) setComparisons(config.comparisons);
                if (config.prepaymentData) setPrepaymentData(config.prepaymentData);
                if (config.amortizationViewMode) setAmortizationViewMode(config.amortizationViewMode);
                if (config.amortizationYear) setAmortizationYear(config.amortizationYear);

                // Clean URL
                window.history.replaceState({}, document.title, window.location.pathname);
            } catch (error) {
                console.error('Error loading shared configuration:', error);
            }
        }
    }, []);

    // Scroll to top on route change
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location.pathname]);

    const isCalculatorPage = location.pathname === '/';

    return (
        <div className="container fade-in">
            <header style={{ marginBottom: 'var(--spacing-lg)', textAlign: 'center' }}>
                <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', marginBottom: '1rem' }}>
                        <img src="/logo.svg" alt="Principal Saver Logo" style={{ width: '48px', height: '48px' }} />
                        <h1 style={{ margin: 0 }}>Principal Saver</h1>
                    </div>
                </Link>
                <p style={{ fontSize: '1.25rem', color: 'var(--text-secondary)' }}>
                    Advanced Home Loan Calculator & Analysis Tool
                </p>

                {/* GrowthSaver Link with Tooltip */}
                <a
                    href="https://growthsaver.vercel.app/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-explore"
                    title="Plan your investments, analyze ETFs, and project portfolio growth with our AI-powered tool"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                        <polyline points="16 7 22 7 22 13"></polyline>
                    </svg>
                    <span>Try GrowthSaver</span>
                </a>

                {/* Navigation Menu */}
                {!isCalculatorPage && (
                    <nav style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
                        <Link
                            to="/"
                            style={{
                                textDecoration: 'none',
                                color: location.pathname === '/' ? 'var(--primary)' : 'var(--text-secondary)',
                                fontWeight: location.pathname === '/' ? '600' : '500',
                                transition: 'color 0.2s ease'
                            }}
                        >
                            Calculator
                        </Link>
                        <Link
                            to="/blog"
                            style={{
                                textDecoration: 'none',
                                color: location.pathname.startsWith('/blog') ? 'var(--primary)' : 'var(--text-secondary)',
                                fontWeight: location.pathname.startsWith('/blog') ? '600' : '500',
                                transition: 'color 0.2s ease'
                            }}
                        >
                            Blog
                        </Link>
                        <Link
                            to="/resources"
                            style={{
                                textDecoration: 'none',
                                color: location.pathname === '/resources' ? 'var(--primary)' : 'var(--text-secondary)',
                                fontWeight: location.pathname === '/resources' ? '600' : '500',
                                transition: 'color 0.2s ease'
                            }}
                        >
                            Resources
                        </Link>
                        <Link
                            to="/faq"
                            style={{
                                textDecoration: 'none',
                                color: location.pathname === '/faq' ? 'var(--primary)' : 'var(--text-secondary)',
                                fontWeight: location.pathname === '/faq' ? '600' : '500',
                                transition: 'color 0.2s ease'
                            }}
                        >
                            FAQ
                        </Link>
                        <Link
                            to="/about"
                            style={{
                                textDecoration: 'none',
                                color: location.pathname === '/about' ? 'var(--primary)' : 'var(--text-secondary)',
                                fontWeight: location.pathname === '/about' ? '600' : '500',
                                transition: 'color 0.2s ease'
                            }}
                        >
                            About
                        </Link>
                    </nav>
                )}
            </header>

            <Routes>
                <Route
                    path="/"
                    element={
                        <CalculatorPage
                            loanData={loanData}
                            setLoanData={setLoanData}
                            manualOverrides={manualOverrides}
                            setManualOverrides={setManualOverrides}
                            comparisons={comparisons}
                            setComparisons={setComparisons}
                            prepaymentData={prepaymentData}
                            setPrepaymentData={setPrepaymentData}
                            amortizationViewMode={amortizationViewMode}
                            setAmortizationViewMode={setAmortizationViewMode}
                            amortizationYear={amortizationYear}
                            setAmortizationYear={setAmortizationYear}
                            fileInputRef={fileInputRef}
                        />
                    }
                />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/resources" element={<Resources />} />
                <Route path="/about" element={<About />} />
            </Routes>

            <Disclaimer />
            <Footer />
        </div>
    );
}

export default App;

